// Activity payload + scoring schemas for the content engine. docs/replan-v2.md R5.
//
// One Zod object per activity `type`, mirroring the `activity_type` enum in
// packages/db/src/schema/enums.ts. R5 ships 6 of the 14 enum values:
//   letter_card, vocabulary_card, tap_match, multiple_choice,
//   listen_choose, quiz_summary
//
// Same discipline as the marketing blocks (see blocks.ts): every field ending
// in `_key` is a translation key resolved by the renderer at render time —
// activity payloads NEVER hold literal user-facing strings. Arabic text is
// authored with full Tashkeel; the renderer strips it per the learner's
// tashkeel_mode (CLAUDE.md §3).
//
// `mediaRef` is an R2 object key relative to the public bucket
// (R2_PUBLIC_BASE_URL), e.g. "lessons/<id>/audio/alif.mp3". The renderer
// prefixes the CDN base; nothing here is an absolute URL.
//
// Adding a new activity type = (1) add the enum value (migration),
// (2) add a payload + scoring schema here + register in the maps below,
// (3) add a renderer in packages/content-engine, (4) wire the server scorer.

import { z } from 'zod';

// ─── Primitives ─────────────────────────────────────────────────────────────

// A non-empty namespaced translation key, e.g. "lesson.alif.q1.prompt".
// Same shape the i18n runtime + marketing blocks use.
export const contentKeyRef = z
  .string()
  .min(1)
  .max(240)
  .regex(/^[a-z][a-z0-9._-]*$/, 'lowercase, dots, dashes only');

const optContentKeyRef = contentKeyRef.optional().nullable();

// R2 public-bucket object key (relative). The renderer prefixes the CDN base.
export const mediaRef = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(/^[a-z0-9][a-z0-9/_.-]*$/i, 'relative r2 key — no leading slash, no scheme');

const optMediaRef = mediaRef.optional().nullable();

// A short stable identifier for an option/item inside an activity payload.
// Used to correlate the learner's answer with the correct choice server-side.
export const optionId = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'lowercase id');

export const activityTypeR5 = z.enum([
  'letter_card',
  'vocabulary_card',
  'tap_match',
  'multiple_choice',
  'listen_choose',
  'quiz_summary',
]);
export type ActivityTypeR5 = z.infer<typeof activityTypeR5>;

// The full 14-type enum, mirroring `activity_type` in db/schema/enums.ts.
// `activityTypeR5` above is the original 6-type subset kept for the parts of the
// scorer/DTOs that were written against it; new code should reference this.
export const activityType = z.enum([
  'intro_video',
  'letter_card',
  'vocabulary_card',
  'tap_match',
  'drag_sort',
  'multiple_choice',
  'fill_blank',
  'listen_choose',
  'record_repeat',
  'letter_trace',
  'read_aloud_passage',
  'story_scene',
  'quiz_summary',
  'printable_homework',
]);
export type ActivityType = z.infer<typeof activityType>;

// ─── 1. letter_card ──────────────────────────────────────────────────────────
// A single Arabic letter with its name, sound, and an example word. Presentational
// (no scoring) — the learner taps "continue".

export const letterCardData = z.object({
  // The letter glyph itself, authored with Tashkeel (e.g. "أَ").
  glyph: z.string().min(1).max(8),
  nameKey: contentKeyRef, // letter name, e.g. "ألف"
  soundKey: optContentKeyRef, // phonetic hint
  exampleWordKey: optContentKeyRef,
  exampleGlossKey: optContentKeyRef,
  audioName: optMediaRef, // audio of the letter name/sound
  audioExample: optMediaRef,
  imageExample: optMediaRef,
});
export type LetterCardData = z.infer<typeof letterCardData>;

// ─── 2. vocabulary_card ──────────────────────────────────────────────────────
// Image + Arabic word + English gloss + audio. Presentational.

export const vocabularyCardData = z.object({
  wordKey: contentKeyRef, // Arabic word (with Tashkeel)
  glossKey: optContentKeyRef, // English gloss
  image: optMediaRef,
  audio: optMediaRef,
});
export type VocabularyCardData = z.infer<typeof vocabularyCardData>;

// ─── 3. tap_match ────────────────────────────────────────────────────────────
// Pair each prompt (image/word) with its match. Scored: all pairs correct = 100.

const tapMatchPair = z.object({
  id: optionId,
  promptKey: contentKeyRef, // left item (word)
  promptImage: optMediaRef,
  matchKey: contentKeyRef, // right item (gloss / paired word)
});

export const tapMatchData = z
  .object({
    instructionKey: optContentKeyRef,
    pairs: z.array(tapMatchPair).min(2).max(6),
  })
  .refine((d) => new Set(d.pairs.map((p) => p.id)).size === d.pairs.length, {
    message: 'pair ids must be unique',
    path: ['pairs'],
  });
export type TapMatchData = z.infer<typeof tapMatchData>;

// ─── 4. multiple_choice ──────────────────────────────────────────────────────
// One prompt, N options, single correct answer. `correctId` lives in the
// payload but the server NEVER ships it to the client (the kids route strips it).

const mcOption = z.object({
  id: optionId,
  labelKey: contentKeyRef,
  image: optMediaRef,
});

export const multipleChoiceData = z
  .object({
    promptKey: contentKeyRef,
    promptAudio: optMediaRef,
    options: z.array(mcOption).min(2).max(4),
    correctId: optionId,
  })
  .refine((d) => d.options.some((o) => o.id === d.correctId), {
    message: 'correctId must match an option id',
    path: ['correctId'],
  })
  .refine((d) => new Set(d.options.map((o) => o.id)).size === d.options.length, {
    message: 'option ids must be unique',
    path: ['options'],
  });
export type MultipleChoiceData = z.infer<typeof multipleChoiceData>;

// ─── 5. listen_choose ────────────────────────────────────────────────────────
// Play audio, choose the matching image/word. Same scoring shape as MC, but the
// prompt is an audio clip rather than text.

export const listenChooseData = z
  .object({
    audio: mediaRef, // required — the thing being listened to
    instructionKey: optContentKeyRef,
    options: z.array(mcOption).min(2).max(4),
    correctId: optionId,
  })
  .refine((d) => d.options.some((o) => o.id === d.correctId), {
    message: 'correctId must match an option id',
    path: ['correctId'],
  })
  .refine((d) => new Set(d.options.map((o) => o.id)).size === d.options.length, {
    message: 'option ids must be unique',
    path: ['options'],
  });
export type ListenChooseData = z.infer<typeof listenChooseData>;

// ─── 6. quiz_summary ─────────────────────────────────────────────────────────
// Auto-generated end-of-lesson check: a small bank of single-answer questions.
// Score = (correct / total) * 100.

const quizQuestion = z
  .object({
    id: optionId,
    promptKey: contentKeyRef,
    options: z.array(mcOption).min(2).max(4),
    correctId: optionId,
  })
  .refine((q) => q.options.some((o) => o.id === q.correctId), {
    message: 'correctId must match an option id',
    path: ['correctId'],
  });

export const quizSummaryData = z
  .object({
    titleKey: optContentKeyRef,
    questions: z.array(quizQuestion).min(1).max(8),
  })
  .refine((d) => new Set(d.questions.map((q) => q.id)).size === d.questions.length, {
    message: 'question ids must be unique',
    path: ['questions'],
  });
export type QuizSummaryData = z.infer<typeof quizSummaryData>;

// ─── 7. intro_video ──────────────────────────────────────────────────────────
// A video plays, the learner taps continue. Presentational. `poster` is an
// optional still shown before playback; `captions` is a WebVTT track (a11y §28).

export const introVideoData = z.object({
  video: mediaRef,
  poster: optMediaRef,
  captions: optMediaRef, // .vtt track — captions are required on shipped video
  titleKey: optContentKeyRef,
});
export type IntroVideoData = z.infer<typeof introVideoData>;

// ─── 8. drag_sort ────────────────────────────────────────────────────────────
// Drag each item into the correct category (bucket). Scored: every item in its
// right bucket = 100. `categoryId` on each item is the answer key (stripped
// before the payload ships to the client).

const dragCategory = z.object({
  id: optionId,
  labelKey: contentKeyRef,
});

const dragItem = z.object({
  id: optionId,
  labelKey: contentKeyRef,
  image: optMediaRef,
  categoryId: optionId, // correct bucket — answer key
});

export const dragSortData = z
  .object({
    instructionKey: optContentKeyRef,
    categories: z.array(dragCategory).min(2).max(4),
    items: z.array(dragItem).min(2).max(8),
  })
  .refine((d) => new Set(d.categories.map((c) => c.id)).size === d.categories.length, {
    message: 'category ids must be unique',
    path: ['categories'],
  })
  .refine((d) => new Set(d.items.map((i) => i.id)).size === d.items.length, {
    message: 'item ids must be unique',
    path: ['items'],
  })
  .refine((d) => d.items.every((i) => d.categories.some((c) => c.id === i.categoryId)), {
    message: 'each item categoryId must match a category id',
    path: ['items'],
  });
export type DragSortData = z.infer<typeof dragSortData>;

// ─── 9. fill_blank ───────────────────────────────────────────────────────────
// A sentence with one or more blanks. Each blank has a set of accepted answers
// (case/tashkeel-insensitive match, done server-side). An optional word bank
// gives tappable choices. `segments` render the sentence: text segments are
// translation keys, blank segments reference a blank by id.
//
// The `accepted` arrays are answer keys — stripped before shipping.

const fillSegment = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), textKey: contentKeyRef }),
  z.object({ kind: z.literal('blank'), blankId: optionId }),
]);

const fillBlank = z.object({
  id: optionId,
  // Accepted answers, authored WITH tashkeel; the server normalises before
  // comparing (strips tashkeel + lowercases + trims).
  accepted: z.array(z.string().trim().min(1).max(80)).min(1).max(6),
});

export const fillBlankData = z
  .object({
    instructionKey: optContentKeyRef,
    segments: z.array(fillSegment).min(1).max(20),
    blanks: z.array(fillBlank).min(1).max(6),
    // Optional tappable word bank (translation keys). When present the client
    // shows chips instead of a free-text field.
    wordBank: z.array(contentKeyRef).max(12).optional(),
  })
  .refine((d) => new Set(d.blanks.map((b) => b.id)).size === d.blanks.length, {
    message: 'blank ids must be unique',
    path: ['blanks'],
  })
  .refine(
    (d) => {
      const blankIds = new Set(d.blanks.map((b) => b.id));
      const usedIds = d.segments
        .filter((s): s is { kind: 'blank'; blankId: string } => s.kind === 'blank')
        .map((s) => s.blankId);
      // every blank appears exactly once in the segments, and vice versa
      return (
        usedIds.length === blankIds.size &&
        usedIds.every((id) => blankIds.has(id)) &&
        new Set(usedIds).size === usedIds.length
      );
    },
    { message: 'each blank must be referenced exactly once by a segment', path: ['segments'] },
  );
export type FillBlankData = z.infer<typeof fillBlankData>;

// ─── 10. record_repeat ───────────────────────────────────────────────────────
// The learner records their voice repeating a phrase. Presentational until
// Phase 2 (Whisper grading, CLAUDE.md §16); for now "recorded" = viewed. The
// model phrase audio + text are shown for the learner to imitate.

export const recordRepeatData = z.object({
  phraseKey: contentKeyRef, // the phrase to repeat (with tashkeel)
  glossKey: optContentKeyRef,
  modelAudio: optMediaRef, // reference pronunciation
});
export type RecordRepeatData = z.infer<typeof recordRepeatData>;

// ─── 11. letter_trace ────────────────────────────────────────────────────────
// Trace an Arabic letter along an SVG stroke path. Stroke-order evaluation lands
// in Phase 3 (CLAUDE.md §26.6); for now completing the trace = viewed. `strokes`
// are SVG path `d` strings drawn in order as a guide.

export const letterTraceData = z.object({
  glyph: z.string().min(1).max(8), // the letter, with tashkeel
  nameKey: optContentKeyRef,
  // Ordered SVG path `d` strings — the guide overlay the learner traces.
  strokes: z.array(z.string().trim().min(1).max(2000)).min(1).max(8),
  // viewBox for the SVG canvas the strokes are authored against.
  viewBox: z
    .string()
    .trim()
    .regex(/^-?\d+(\.\d+)? -?\d+(\.\d+)? \d+(\.\d+)? \d+(\.\d+)?$/, 'four numbers: minX minY w h')
    .default('0 0 100 100'),
  audioName: optMediaRef,
});
export type LetterTraceData = z.infer<typeof letterTraceData>;

// ─── 12. read_aloud_passage ──────────────────────────────────────────────────
// A paragraph for the learner to read, with an optional TTS/narration clip and
// an inline Tashkeel toggle (the renderer honours the learner's mode, but this
// activity type surfaces a per-passage override toggle). Presentational.

export const readAloudPassageData = z.object({
  titleKey: optContentKeyRef,
  bodyKey: contentKeyRef, // the passage (with full tashkeel)
  audio: optMediaRef, // narration
  // Whether to show the inline tashkeel toggle (advanced learners).
  allowTashkeelToggle: z.boolean().default(true),
});
export type ReadAloudPassageData = z.infer<typeof readAloudPassageData>;

// ─── 13. story_scene ─────────────────────────────────────────────────────────
// A full-screen illustrated story page: illustration + narration + tap-to-reveal
// vocabulary chips. Presentational. `storyId` optionally links a reusable story
// row (schema.stories); the inline fields let a scene stand alone too.

const storyVocab = z.object({
  id: optionId,
  wordKey: contentKeyRef, // Arabic word (with tashkeel)
  glossKey: optContentKeyRef,
  audio: optMediaRef,
});

export const storySceneData = z.object({
  storyId: z.uuid().optional().nullable(),
  image: optMediaRef,
  bodyKey: contentKeyRef, // the scene's paragraph
  audio: optMediaRef, // narration
  vocab: z.array(storyVocab).max(8).optional(),
});
export type StorySceneData = z.infer<typeof storySceneData>;

// ─── 14. printable_homework ──────────────────────────────────────────────────
// A downloadable/emailable PDF worksheet for this lesson. Presentational — the
// learner (or parent) taps to download. `pdf` is an R2 key under printables/.

export const printableHomeworkData = z.object({
  titleKey: contentKeyRef,
  descriptionKey: optContentKeyRef,
  pdf: mediaRef, // printables/<lesson>/<slug>.pdf
});
export type PrintableHomeworkData = z.infer<typeof printableHomeworkData>;

// ─── Registry: type → payload schema ─────────────────────────────────────────
// The admin builder + the seed scripts validate `activities.payload` against
// the right schema by looking it up here.

export const activityDataByType = {
  intro_video: introVideoData,
  letter_card: letterCardData,
  vocabulary_card: vocabularyCardData,
  tap_match: tapMatchData,
  drag_sort: dragSortData,
  multiple_choice: multipleChoiceData,
  fill_blank: fillBlankData,
  listen_choose: listenChooseData,
  record_repeat: recordRepeatData,
  letter_trace: letterTraceData,
  read_aloud_passage: readAloudPassageData,
  story_scene: storySceneData,
  quiz_summary: quizSummaryData,
  printable_homework: printableHomeworkData,
} as const satisfies Record<ActivityType, z.ZodType>;

// Which activity types are graded (submit an answer) vs presentational (viewed).
// The server scorer + the LessonPlayer both consult this. record_repeat and
// letter_trace are presentational until Phase 2/3 grading lands.
export const scoredActivityTypes = [
  'tap_match',
  'drag_sort',
  'multiple_choice',
  'fill_blank',
  'listen_choose',
  'quiz_summary',
] as const satisfies readonly ActivityType[];

export type ScoredActivityType = (typeof scoredActivityTypes)[number];

export function isScoredType(type: ActivityType): type is ScoredActivityType {
  return (scoredActivityTypes as readonly string[]).includes(type);
}

// ─── Scoring rules ───────────────────────────────────────────────────────────
// Per-activity weighting consumed by the server scorer + XP award. `passScore`
// is the floor (0-100) below which an activity counts as "needs another try";
// `xp` is the reward on a correct/complete attempt.

export const scoringRules = z.object({
  // 0 for presentational types (letter_card, vocabulary_card).
  passScore: z.number().int().min(0).max(100).default(0),
  xp: z.number().int().min(0).max(100).default(5),
  // Presentational activities are "complete" the moment they're viewed.
  graded: z.boolean().default(true),
});
export type ScoringRules = z.infer<typeof scoringRules>;

// ─── Attempt submission (client → /kids/activities/:id/attempt) ──────────────
// One discriminated shape per scored type. Presentational types submit `{ kind:
// 'viewed' }`. The server re-scores against the stored payload — it NEVER trusts
// a client-sent score.

export const attemptInput = z.discriminatedUnion('kind', [
  // letter_card + vocabulary_card: just viewed.
  z.object({ kind: z.literal('viewed') }),
  // multiple_choice + listen_choose: one chosen option id.
  z.object({ kind: z.literal('choose'), chosenId: optionId }),
  // tap_match: a map of pair id → the match the learner attached to it.
  z.object({
    kind: z.literal('match'),
    pairs: z
      .array(z.object({ id: optionId, matchedId: optionId }))
      .min(1)
      .max(6),
  }),
  // quiz_summary: an answer per question.
  z.object({
    kind: z.literal('quiz'),
    answers: z
      .array(z.object({ questionId: optionId, chosenId: optionId }))
      .min(1)
      .max(8),
  }),
  // drag_sort: which bucket the learner dropped each item into.
  z.object({
    kind: z.literal('sort'),
    placements: z
      .array(z.object({ itemId: optionId, categoryId: optionId }))
      .min(1)
      .max(8),
  }),
  // fill_blank: the learner's answer for each blank (free text or a bank word).
  z.object({
    kind: z.literal('fill'),
    answers: z
      .array(z.object({ blankId: optionId, value: z.string().trim().max(80) }))
      .min(1)
      .max(6),
  }),
]);
export type AttemptInput = z.infer<typeof attemptInput>;

// ─── Response DTOs (server → kids app) ───────────────────────────────────────
// The activity DTO the kids route returns has the answer key STRIPPED — the
// client only ever sees prompts + options, never `correctId`.

export const activityDto = z.object({
  id: z.uuid(),
  type: activityType,
  sortOrder: z.number().int(),
  // Payload with answer keys removed (correctId stripped by the route).
  payload: z.unknown(),
  scoringRules,
});
export type ActivityDto = z.infer<typeof activityDto>;

export const lessonDto = z.object({
  id: z.uuid(),
  unitId: z.uuid(),
  titleKey: z.string(),
  summaryKey: z.string().nullable(),
  estMinutes: z.number().int(),
  subjectSlug: z.string(),
  grade: z.string(),
  activities: z.array(activityDto),
});
export type LessonDto = z.infer<typeof lessonDto>;

export const attemptResultDto = z.object({
  activityId: z.uuid(),
  score: z.number().int().min(0).max(100),
  correct: z.boolean(),
  passed: z.boolean(),
  xpAwarded: z.number().int().min(0),
});
export type AttemptResultDto = z.infer<typeof attemptResultDto>;

export const lessonCompleteResultDto = z.object({
  lessonId: z.uuid(),
  score: z.number().int().min(0).max(100),
  xpAwarded: z.number().int().min(0),
  streak: z.object({ current: z.number().int(), longest: z.number().int() }),
  // Badges this completion unlocked — the player celebrates them. Empty is the
  // common case; the field is always present so the client can map over it.
  badgesEarned: z
    .array(
      z.object({
        id: z.uuid(),
        slug: z.string(),
        titleKey: z.string(),
        descriptionKey: z.string().nullable(),
        iconKey: z.string().nullable(),
      }),
    )
    .default([]),
});
export type LessonCompleteResultDto = z.infer<typeof lessonCompleteResultDto>;

// ─── Gamification: badge rules + DTOs ───────────────────────────────────────
// `badges.rule` is a typed JSONB discriminated on `kind`. The award engine
// (apps/api/src/lib/badges.ts) evaluates every rule after a lesson completes;
// `learner_badges` has a unique (user, badge) index so awarding is idempotent.
//
// Adding a rule kind = add a variant here + a branch in the engine + seed a row.

export const badgeRule = z.discriminatedUnion('kind', [
  // Completed at least `n` lessons (any track).
  z.object({ kind: z.literal('lesson_count'), n: z.number().int().min(1).max(1000) }),
  // Current streak reached `n` days.
  z.object({ kind: z.literal('streak_days'), n: z.number().int().min(1).max(365) }),
  // Scored 100 on at least `n` lessons.
  z.object({ kind: z.literal('perfect_lesson'), n: z.number().int().min(1).max(1000).default(1) }),
  // Earned at least `n` total XP.
  z.object({ kind: z.literal('xp_total'), n: z.number().int().min(1).max(1_000_000) }),
  // Completed every published lesson in `n` units (subject-agnostic).
  z.object({ kind: z.literal('unit_complete'), n: z.number().int().min(1).max(100).default(1) }),
]);
export type BadgeRule = z.infer<typeof badgeRule>;

export const badgeDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  titleKey: z.string(),
  descriptionKey: z.string().nullable(),
  iconKey: z.string().nullable(),
  earnedAt: z.string().nullable(), // null = not yet earned (locked)
});
export type BadgeDto = z.infer<typeof badgeDto>;

// ─── Admin CMS inputs (admin app → /admin/content/*) ─────────────────────────
// The tree navigator + typed activity builder write through these. Title/body
// fields are translation keys (the admin translation editor owns the strings);
// the CMS just references them. `activity.data` is validated against
// `activityDataByType[type]` in the route handler (same pattern as page blocks).

const gradeRef = z.enum(['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6']);
const trackVariantRef = z.enum(['standard', 'native', 'non_native_speaker']);
const localeRef = z.enum(['ar', 'en']);
const subjectSlugRef = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_-]*$/, 'lowercase slug');

// subjects
export const createSubjectInput = z.object({
  slug: subjectSlugRef,
  nameKey: contentKeyRef,
  icon: z.string().trim().max(60).optional().nullable(),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type CreateSubjectInput = z.infer<typeof createSubjectInput>;

export const updateSubjectInput = z.object({
  nameKey: contentKeyRef.optional(),
  icon: z.string().trim().max(60).optional().nullable(),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type UpdateSubjectInput = z.infer<typeof updateSubjectInput>;

// tracks
export const createTrackInput = z.object({
  subjectId: z.uuid(),
  grade: gradeRef,
  variant: trackVariantRef.default('standard'),
  language: localeRef.default('ar'),
  titleKey: contentKeyRef,
});
export type CreateTrackInput = z.infer<typeof createTrackInput>;

export const updateTrackInput = z.object({
  titleKey: contentKeyRef.optional(),
});
export type UpdateTrackInput = z.infer<typeof updateTrackInput>;

// units
export const createUnitInput = z.object({
  trackId: z.uuid(),
  titleKey: contentKeyRef,
  descriptionKey: optContentKeyRef,
  coverMediaId: z.uuid().optional().nullable(),
  sortOrder: z.number().int().nonnegative().optional(),
  // Free-tier boundary: a free unit is playable without a subscription.
  isFree: z.boolean().optional(),
});
export type CreateUnitInput = z.infer<typeof createUnitInput>;

export const updateUnitInput = z.object({
  titleKey: contentKeyRef.optional(),
  descriptionKey: optContentKeyRef,
  coverMediaId: z.uuid().optional().nullable(),
  sortOrder: z.number().int().nonnegative().optional(),
  isFree: z.boolean().optional(),
});
export type UpdateUnitInput = z.infer<typeof updateUnitInput>;

// lessons
export const createLessonInput = z.object({
  unitId: z.uuid(),
  titleKey: contentKeyRef,
  summaryKey: optContentKeyRef,
  coverMediaId: z.uuid().optional().nullable(),
  estMinutes: z.number().int().min(1).max(240).optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type CreateLessonInput = z.infer<typeof createLessonInput>;

export const updateLessonInput = z.object({
  titleKey: contentKeyRef.optional(),
  summaryKey: optContentKeyRef,
  coverMediaId: z.uuid().optional().nullable(),
  estMinutes: z.number().int().min(1).max(240).optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type UpdateLessonInput = z.infer<typeof updateLessonInput>;

// activities — `type` fixed at create; `data` + `scoringRules` revalidated on
// update against the activity's own type (looked up server-side).
export const createActivityInput = z.object({
  lessonId: z.uuid(),
  type: activityType,
  sortOrder: z.number().int().nonnegative().optional(),
  data: z.unknown(), // validated against activityDataByType[type] in the route
  scoringRules: scoringRules.optional(),
});
export type CreateActivityInput = z.infer<typeof createActivityInput>;

export const updateActivityInput = z.object({
  data: z.unknown().optional(),
  scoringRules: scoringRules.optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type UpdateActivityInput = z.infer<typeof updateActivityInput>;

// Bulk reorder — the full ordered id list for one parent's children.
export const reorderInput = z.object({
  ids: z.array(z.uuid()).min(1).max(200),
});
export type ReorderInput = z.infer<typeof reorderInput>;

// Publish / set review state on a unit, lesson, or activity.
export const contentEntityTypeRef = z.enum(['unit', 'lesson', 'activity']);
export type ContentEntityType = z.infer<typeof contentEntityTypeRef>;

export const setPublishStateInput = z.object({
  status: z.enum(['draft', 'in_review', 'published']),
});
export type SetPublishStateInput = z.infer<typeof setPublishStateInput>;
